// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Fetch cart transaction line items from the local API endpoint.
//
// See `server/api/cart-line-items.js` to see what data should
// be sent in the body.
export const cartTransactionLineItems = body => {
  return post('/api/cart-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

export const initiateCartPrivileged = body => {
  return post('/api/initiate-cart-privileged', body);
};

// Confirm the child stock reservations of a cart purchase after payment.
// See `server/api/confirm-cart-stock.js`.
export const confirmCartStock = body => {
  return post('/api/confirm-cart-stock', body);
};

// Initiate an inquiry transaction (and send its first message) through the
// backend. The inquiry transition is privileged, so it can only be created from
// a trusted server context — this endpoint enforces email verification before
// letting a user message a seller. The server reads the authenticated user from
// the session; the body only carries the transition, process alias, listing id
// and message.
//
// See `server/api/initiate-inquiry-privileged.js`.
export const initiateInquiryPrivileged = body => {
  return post('/api/initiate-inquiry-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check whether an email already has an account, so the signup form can warn at
// the email step instead of after the whole form is filled in. Returns
// { available: boolean }. Fails open server-side (returns available: true) if
// the lookup can't be performed, and the final signup submit still enforces
// uniqueness. See `server/api/check-email-availability.js`.
export const checkEmailAvailability = body => {
  return post('/api/check-email-availability', body);
};

export const checkShipmentPrice = body => {
  return post('/api/shipping/check-shipment-price', body);
};

export const createLabel = body => {
  return post('/api/shipping/create-label', body);
};

export const updateTransactionMetadata = body => {
  return post('/api/update-tx-metadata', body);
};

export const cleanup = () => {
  return post('/api/auth/verify-invitation', {});
};

export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

export const reportListing = body => {
  return post('/api/report-listing', body);
};

export const reportMessage = body => {
  return post('/api/report-message', body);
};

// Notify the server that a marketing-opt-in event happened for the
// currently logged-in user. The server reads authoritative user state
// (email, name, emailVerified, marketingOptIn) from Sharetribe via the
// session, so the body only needs to identify the eventType
// (e.g. 'signup', 'email-verified', 'account-update').
export const notifyMarketingOptIn = body => {
  return window
    .fetch(`${apiBaseUrl()}/api/notify-marketing-opt-in`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    .catch(() => {
      // Fire-and-forget: never block signup/profile-save on this.
    });
};

// Notify the server that the current user has just published a
// listing. The server reads authoritative user state from Sharetribe
// via the session and forwards to n8n, which adds the user to the
// Seller MailerLite group iff they're opted in and verified.
// Fire-and-forget: never blocks the publish flow.
export const notifyListingPublished = () => {
  return window
    .fetch(`${apiBaseUrl()}/api/notify-listing-published`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    .catch(() => {});
};

// Asynchronously moderate a piece of user content (a message or a display
// name). The server reads the authoritative user identity from the session,
// so the body only needs the trigger type, the content and an optional
// transaction id. Fire-and-forget: never blocks or affects the user flow.
export const moderateContent = body => {
  return window
    .fetch(`${apiBaseUrl()}/api/moderate-content`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    .catch(() => {});
};

// Ensure the current user has their access-control rights. The marketplace
// denies both transaction and posting rights to new users by default; this
// grants them on the right schedule (transactions early; posting once email is
// verified). The server reads the authenticated user from the session and is
// idempotent/self-guarding, so this is safe to call on every authenticated
// load. Fire-and-forget — never blocks the flow.
export const ensureUserPermissions = () => {
  return window
    .fetch(`${apiBaseUrl()}/api/ensure-user-permissions`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    .catch(() => {});
};

// Fire-and-forget: tell the server a message was just sent in a conversation,
// so it can mark it unread for the other party (see server/api/message-sent.js).
// Never throws; a lost call only means one missing unread marker.
export const notifyMessageSent = txId => {
  const id = typeof txId === 'string' ? txId : txId?.uuid;
  if (!id || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return Promise.resolve();
  }
  // Started inside a promise so nothing can throw into the send flow that called it.
  return Promise.resolve()
    .then(() =>
      window.fetch(`${apiBaseUrl()}/api/message-sent`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ txId: id }),
      })
    )
    .catch(() => {});
};
